import React, { useEffect, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useScrollToAnchor } from 'common/customHooks';
import './LocalPage.css';
import TrackingService from 'common/services/Tracking/Tracking';
import StateContext from 'common/contexts/state-context';
import Map from 'common/components/Map/Map';
import { HAIRNET_ID, ROOT_IMG_URL } from '../../GlobalValues';
import MandalaCard from '../../widgets/MandalaCard/MandalaCard';
import OpeningHours from './widgets/OpeningHours/OpeningHours';
import { MergeFieldCta, SimpleCta } from './widgets/SimpleCard/SimpleCard';
import NearbyPlacesWrapper from './widgets/NearbyPlacesWrapper/NearbyPlacesWrapper';
import MerchantPreview from './widgets/MerchantPreview/MerchantPreview';
import Description from './widgets/Description/Description';
import Hairnet from './widgets/Hairnet/Hairnet';
import GooddaysReviewWidget from './widgets/GooddaysReviewWidget/GooddaysReviewWidget';
import MerchantName from './widgets/MerchantName/MerchantName';
import Networks from './widgets/Networks/Networks';
import MerchantAddress from './widgets/MerchantAddress/MerchantAddress';
import GooddaysReviewNoteWidget from './widgets/GooddaysReviewNoteWidget/GooddaysReviewNoteWidget';
import Phone from './widgets/Phone/Phone';
import Card from './widgets/Card/Card';
import PictureTextRF from './widgets/Discover/Discover';
import RichFieldGroup from 'common/components/RichFieldGroup/RichFieldGroup';
import {
  RICHFIELDS_CONFIG_DESKTOP,
  RICHFIELDS_CONFIG_MOBILE,
  RICHFIELDS_KEYS,
} from './richfieldsConfig/richfieldsConfig';
import { CollectionWrapper } from '../../widgets/Collection/Collection';
import GooddaysReviews from './widgets/GooddaysReviews/GooddaysReviews';
import { useLocation } from 'react-router-dom';
import ExtractorService from 'common/services/Extractor';

const LocalPage = () => {
  const [state] = useContext(StateContext);
  const { storeId } = useParams();
  const location = useLocation();
  const { scrollToAnchorById } = useScrollToAnchor({
    behavior: 'smooth',
    block: 'start',
    offset: 80,
  });

  useEffect(() => {
    if (location.hash) {
      const anchorId = location.hash.substring(1);
      const delay = 500;
      const scrollTimeout = setTimeout(() => {
        const success = scrollToAnchorById(anchorId);

        if (!success) {
          scrollToAnchorById(anchorId);
        }
      }, delay);

      return () => clearTimeout(scrollTimeout);
    }
  }, [location, scrollToAnchorById]);

  useEffect(() => {
    if (storeId) {
      TrackingService.pageView('localpage', storeId);
    }
  }, [storeId]);

  const networks = useMemo(() => {
    const result = [];
    const facebook = state?.merchant?.extended_data?.extended_string_data_1;
    const instagram = state?.merchant?.extended_data?.extended_string_data_2;
    if (facebook) {
      result.push({
        icon: 'faFacebook',
        url: facebook,
      });
    }
    if (instagram) {
      result.push({
        icon: 'faInstagram',
        url: instagram,
      });
    }
    return result;
  }, [state.merchant]);

  return (
    <React.Fragment>
      <Map mapSize={{ height: '0', width: '0' }} />
      {state.lp && (
        <article
          className="sl-flex sl-flex-col sl-bg-fag"
          itemScope
          itemType="https://schema.org/HairSalon"
          style={{
            backgroundImage: `url(${ROOT_IMG_URL}/rawmandala.png)`,
            backgroundRepeat: 'no-repeat',
            backgroundPositionX: '75%',
            backgroundPositionY: '45%',
            backgroundSize: '25%',
            paddingTop: 'var(--nav-height)',
          }}
        >
          <div className="desktop-only">
            <div className="sl-flex sl-flex-row sl-gap-xxl sl-container sl-gutter-l">
              <Desktop state={state} networks={networks}></Desktop>
            </div>
          </div>
          <div className="mobile-only">
            <div className="sl-flex sl-flex-col">
              <Mobile state={state} networks={networks}></Mobile>
            </div>
          </div>
        </article>
      )}
    </React.Fragment>
  );
};

const Mobile = ({ state, networks }) => {
  const blocsMaxHeight = '200px';
  return (
    <>
      <MerchantPreview config={RICHFIELDS_CONFIG_MOBILE} />
      <div className="sl-flex sl-flex-col mobile-container sl-bg-fag sl-gap-l">
        <div className="sl-flex sl-flex-col sl-gap-l sl-bg-primary sl-gutter-m">
          <hgroup className="sl-flex sl-flex-col sl-gap-l">
            <div className="sl-flex sl-ai-center sl-gap sl-jc-center">
              <MerchantName name={state.merchant.name} />
              <Networks networks={networks} />
            </div>

            <MerchantAddress merchant={state.merchant} />
            <GooddaysReviewNoteWidget
              field={state.merchant.extended_data.extended_string_data_8}
              reference={state.merchant.reference}
            />
            <div className="sl-flex sl-gap inline-mobile-ctas" role="navigation" aria-label="Actions principales">
              <HairnetScrollTo merchant={state.merchant} icon={'faCalendarDays'} message="PRENDRE RENDEZ VOUS" />
              <Phone merchant={state.merchant} />
              <div className="presta-wrap">
                <HairnetScrollTo merchant={state.merchant} message="NOS PRESTATIONS" icon="faCalendarDays" />
              </div>
            </div>
          </hgroup>
          <OpeningHours merchant={state.merchant}></OpeningHours>
          <GooddaysReviews reviews={state.goodays.reviews} />
          <Description state={state} />
          <NearbyPlacesWrapper></NearbyPlacesWrapper>
        </div>
      </div>
      <div
        style={{
          padding: 'var(--gutter-l) 0',
        }}
      >
        <MandalaCard title="EN CE MOMENT" style={{ overflow: 'hidden' }}>
          <RichFieldGroup
            groupId={RICHFIELDS_KEYS.MANDALA_CARD}
            style={RICHFIELDS_CONFIG_DESKTOP[RICHFIELDS_KEYS.MANDALA_CARD]}
          ></RichFieldGroup>
        </MandalaCard>
      </div>
      <div className="sl-flex sl-flex-col mobile-container sl-bg-fag sl-gap-l">
        <div className="sl-flex sl-flex-col sl-gap-l sl-gutter-m force-tertiary-color">
          <RichFieldGroup
            groupId={RICHFIELDS_KEYS.INFOS_BLOC}
            style={RICHFIELDS_CONFIG_DESKTOP[RICHFIELDS_KEYS.INFOS_BLOC]}
          ></RichFieldGroup>
          <Hairnet merchant={state.merchant} />
        </div>
      </div>
      <div className="sl-flex sl-flex-col sl-gap-l" style={{ marginTop: 'var(--gutter-m)' }}>
        <CollectionWrapper style={{ maxHeight: blocsMaxHeight, width: '100%' }}>
          <RichFieldGroup
            groupId={RICHFIELDS_KEYS.COLLECTION}
            style={RICHFIELDS_CONFIG_MOBILE[RICHFIELDS_KEYS.COLLECTION]}
          ></RichFieldGroup>
        </CollectionWrapper>
        <PictureTextRF
          groupId={RICHFIELDS_KEYS.DISCOVER_CARD}
          groupStyle={RICHFIELDS_CONFIG_MOBILE[RICHFIELDS_KEYS.DISCOVER_CARD]}
          text="DÉCOUVREZ VOG COIFFURE"
          img="preview-fb-5.jpg"
          href="https://www.google.fr"
        />
      </div>
      <div className="sl-flex sl-flex-col mobile-container sl-bg-fag sl-gap-l">
        <div className="sl-flex sl-flex-col sl-gap-l sl-ai-center">
          <GooddaysReviewWidget
            field={state.merchant.extended_data.extended_string_data_8}
            reference={state.merchant.reference}
          />
          <MergeFieldCta merchant={state.merchant} mergeField={9} isMail={true} style={{ width: '100%' }}>
            RECRUTEMENT
          </MergeFieldCta>
        </div>
      </div>
    </>
  );
};

const Desktop = ({ state, networks }) => {
  const blocsMaxHeight = '400px';
  return (
    <main className="sl-grow-1 sl-flex sl-flex-col sl-gap-xl">
      <div className="sl-grow-1 sl-flex sl-gap-xl">
        <div className="sl-grow-1 sl-gap-xl sl-flex sl-flex-col">
          <hgroup className="sl-grow-1 sl-flex sl-flex-col">
            <div className="sl-flex sl-ai-center sl-gap">
              <MerchantName name={state.merchant.name} />
              <Networks networks={networks} />
            </div>
            <Card>
              <div className="sl-flex sl-gap">
                <div className="sl-grow-1 sl-flex sl-flex-col sl-gap-s">
                  <Phone merchant={state.merchant} />
                  <MerchantAddress merchant={state.merchant} />
                </div>
                <GooddaysReviewNoteWidget
                  field={state.merchant.extended_data.extended_string_data_8}
                  reference={state.merchant.reference}
                />
              </div>
              <div style={{ marginTop: '30px' }}>
                <GooddaysReviews reviews={state.goodays.reviews} />
              </div>
            </Card>
          </hgroup>
          <Card>
            <section className="sl-grow-1 sl-flex sl-flex-col sl-bg-primary" aria-label="À propos du salon">
              <div className="sl-flex sl-gap">
                <Description state={state} />
                <figure className="sl-flex-1 sl-flex sl-ai-center" style={{ minWidth: 0 }}>
                  <MerchantPreview config={RICHFIELDS_CONFIG_DESKTOP} />
                </figure>
              </div>
            </section>
          </Card>
          <MandalaCard title="EN CE MOMENT" style={{ overflow: 'hidden' }}>
            <RichFieldGroup
              groupId={RICHFIELDS_KEYS.MANDALA_CARD}
              style={RICHFIELDS_CONFIG_DESKTOP[RICHFIELDS_KEYS.MANDALA_CARD]}
            ></RichFieldGroup>
          </MandalaCard>
          <Hairnet merchant={state.merchant} />
        </div>
        <div className="desktop-only">
          <Aside state={state} />
        </div>
      </div>
      <CollectionWrapper style={{ maxHeight: blocsMaxHeight, width: '100%' }}>
        <RichFieldGroup
          groupId={RICHFIELDS_KEYS.COLLECTION}
          style={RICHFIELDS_CONFIG_DESKTOP[RICHFIELDS_KEYS.COLLECTION]}
        ></RichFieldGroup>
      </CollectionWrapper>
      <PictureTextRF
        text="DÉCOUVREZ VOG COIFFURE"
        groupId={RICHFIELDS_KEYS.DISCOVER_CARD}
        groupStyle={RICHFIELDS_CONFIG_DESKTOP[RICHFIELDS_KEYS.DISCOVER_CARD]}
      />
    </main>
  );
};

const Aside = ({ state }) => {
  return (
    <aside className="sl-flex sl-flex-col sl-gap" style={{ width: '250px' }}>
      <OpeningHours merchant={state.merchant}></OpeningHours>
      <HairnetScrollTo merchant={state.merchant} message="PRENDRE RENDEZ VOUS" icon="faCalendarDays" />
      <HairnetScrollTo merchant={state.merchant} message="NOS PRESTATIONS" />
      <NearbyPlacesWrapper></NearbyPlacesWrapper>
      <div className="force-tertiary-color">
        <RichFieldGroup
          groupId={RICHFIELDS_KEYS.INFOS_BLOC}
          style={RICHFIELDS_CONFIG_DESKTOP[RICHFIELDS_KEYS.INFOS_BLOC]}
        ></RichFieldGroup>
      </div>
      <div className="sl-flex sl-ai-center sl-jc-center">
        <GooddaysReviewWidget
          field={state.merchant.extended_data.extended_string_data_8}
          reference={state.merchant.reference}
        />
      </div>
      <MergeFieldCta merchant={state.merchant} mergeField={9} isMail={true}>
        RECRUTEMENT
      </MergeFieldCta>
    </aside>
  );
};
const HairnetScrollTo = ({ merchant, message, icon }) => {
  const display = merchant?.extended_data?.extended_string_data_3;
  const displayFrame = merchant?.extended_data?.extended_string_data_28;
  const shouldScroll = displayFrame ? ExtractorService.extractBoolean(displayFrame) : false;
  const { scrollToAnchorById } = useScrollToAnchor({
    behavior: 'smooth',
    block: 'start',
    offset: 80,
  });

  if (!display) {
    return null;
  }

  if (shouldScroll) {
    return (
      <div onClick={() => scrollToAnchorById(HAIRNET_ID)} className="sl-cursor-pointer">
        <SimpleCta icon={icon}>{message}</SimpleCta>
      </div>
    );
  }

  return (
    <a href={display} target="_blank" rel="noopener noreferrer">
      <SimpleCta icon={icon}>{message}</SimpleCta>
    </a>
  );
};

export default LocalPage;
